/**
 * Image compression and optimization utilities
 * Reduces egress by compressing images before upload
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

/**
 * Compress an image file to reduce size
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed image as Blob
 */
export async function compressImage(
  file: File | Blob,
  options: CompressionOptions = {}
): Promise<Blob> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.85,
    maxSizeKB = 500,
  } = options;

  // Create image bitmap
  const bitmap = await createImageBitmap(file);

  // Calculate new dimensions while maintaining aspect ratio
  let { width, height } = bitmap;

  if (width > maxWidth || height > maxHeight) {
    const aspectRatio = width / height;

    if (width > height) {
      width = Math.min(width, maxWidth);
      height = width / aspectRatio;
    } else {
      height = Math.min(height, maxHeight);
      width = height * aspectRatio;
    }
  }

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D rendering context');
  }

  // Use better image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);

  // Try to compress to target size
  let compressedBlob = await canvasToBlob(canvas, quality);
  let currentQuality = quality;

  // If still too large, reduce quality iteratively
  while (compressedBlob.size > maxSizeKB * 1024 && currentQuality > 0.3) {
    currentQuality -= 0.1;
    compressedBlob = await canvasToBlob(canvas, currentQuality);
  }

  console.log(`[imageCompression] Original: ${(file.size / 1024).toFixed(1)}KB, Compressed: ${(compressedBlob.size / 1024).toFixed(1)}KB (${(compressedBlob.size / file.size * 100).toFixed(1)}%)`);

  return compressedBlob;
}

/**
 * Convert canvas to blob with quality setting
 */
function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Preset compression options for different use cases
 */
export const COMPRESSION_PRESETS = {
  avatar: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.85,
    maxSizeKB: 100,
  },
  eventImage: {
    maxWidth: 1200,
    maxHeight: 900,
    quality: 0.85,
    maxSizeKB: 500,
  },
  thumbnail: {
    maxWidth: 300,
    maxHeight: 300,
    quality: 0.8,
    maxSizeKB: 50,
  },
} as const;

/**
 * Validate if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
